import type { TablePaginationConfig, TableProps } from 'antd'
import { Button, Card, Form, Input, message, Modal, Select, Switch, Table, Tag } from 'antd'
import { useCallback, useEffect, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'constants/pagination'
import {
  deriveUserRole,
  USER_ROLE,
  USER_ROLE_DESCRIPTIONS,
  USER_ROLE_LABELS,
  USER_ROLE_MARKERS,
  USER_ROLE_OPTIONS,
} from 'constants/user-role'
import { generateRandomPassword } from 'utils'
import { logError, logInfo } from 'utils/logger'

import './style.scss'

interface UserData {
  key: string
  code: string
  userName: string
  email: string
  isEnable2Fa: boolean
  isActive: boolean
  userRole: USER_ROLE | null
}

const USER_ROLE_TAG_COLORS: Record<USER_ROLE, string> = {
  [USER_ROLE.SUPER_ADMIN]: 'red',
  [USER_ROLE.ADMIN]: 'blue',
  [USER_ROLE.CREATOR]: 'gold',
  [USER_ROLE.OPERATOR_MEYREAL]: 'green',
  [USER_ROLE.OPERATOR_MEYFI]: 'purple',
}

// SUPER_ADMIN is provisioned only via BE/DB and cannot be assigned through the UI.
const ASSIGNABLE_ROLE_OPTIONS = USER_ROLE_OPTIONS.filter(
  (opt) => opt.value !== USER_ROLE.SUPER_ADMIN
)

export default function Admin() {
  const additionalItems = [BREADCRUMBS.USER]
  const [data, setData] = useState<UserData[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [current, setCurrent] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [username, setUserName] = useState<string | undefined>(undefined)
  const [activeStatus, setActiveStatus] = useState<number | undefined>(undefined)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [selectedUserCode, setSelectedUserCode] = useState<string | null>(null)
  const [isCreateUserModalVisible, setIsCreateUserModalVisible] = useState(false)
  const [newUserName, setNewUserName] = useState('')
  const [newUserPassword, setNewUserPassword] = useState('')
  const [newUserRole, setNewUserRole] = useState<USER_ROLE | undefined>(undefined)

  const getData = useCallback(
    async (pageIndex = DEFAULT_PAGE_INDEX, pageSize = DEFAULT_PAGE_SIZE) => {
      setLoading(true)
      try {
        const res = await AppAdmin.getListAdmin(pageSize, pageIndex - 1, username, activeStatus)
        if (res?.data.success) {
          const list = res?.data.data.map((item: any, index: number) => {
            const itemPermissions: number[] = Array.isArray(item?.roles) ? item.roles : []
            return {
              key: `${pageIndex}-${index}`,
              code: item?.code,
              userName: item?.userName,
              email: item?.email,
              isEnable2Fa: item?.isEnable2Fa,
              isActive: item?.isActive,
              userRole: deriveUserRole(itemPermissions),
            }
          })
          setData(list)
          setTotal(res?.data.totalCount)
        }
      } catch (error) {
        setData([])
        setTotal(0)
        logError('Error fetching data: ', error)
      } finally {
        setLoading(false)
      }
    },
    [activeStatus, username]
  )

  const handleUpdate = async (payload: any) => {
    setLoading(true)
    try {
      const res = await AppAdmin.updateUser(payload)
      if (res?.data.success) {
        getData(current, pageSize)
        message.success('Update an account sucessfully!')
      }
    } catch (error: any) {
      message.error(error?.message || 'Update an account failed!')
      logError('Error fetching data: ', error)
    } finally {
      setLoading(false)
    }
  }

  const handleRegister = async () => {
    if (!newUserRole) {
      message.error('Please select a user role.')
      return
    }

    setLoading(true)
    try {
      const markerId = USER_ROLE_MARKERS[newUserRole]
      const res = await AppAdmin.createUser({
        userName: newUserName,
        password: newUserPassword,
        roles: [markerId],
      })
      logInfo('createUser response', res?.data)

      if (res?.data.success) {
        const newUserCode = res.data.data?.code
        if (newUserCode) {
          const updateRes = await AppAdmin.updateUser({
            code: newUserCode,
            roles: [markerId],
          })
          logInfo('updateUser (assign role) response', updateRes?.data)
        } else {
          logError('createUser response missing code; cannot assign role', res?.data)
          message.warning('Account created but role assignment skipped (missing user code).')
        }
        getData(current, pageSize)
        message.success('Create an account sucessfully!')
      }
    } catch (error: any) {
      message.error(error?.message || 'Create an account failed!')
      logError('Error fetching data: ', error)
    } finally {
      setIsCreateUserModalVisible(false)
      setLoading(false)
    }
  }

  const showResetPasswordModal = (userCode: string) => {
    setNewPassword(generateRandomPassword())
    setSelectedUserCode(userCode)
    setIsModalVisible(true)
  }

  const handleResetPassword = () => {
    if (selectedUserCode && newPassword) {
      handleUpdate({ code: selectedUserCode, isResetPass: true, resetPass: newPassword })
    }
    setNewPassword('')
    setIsModalVisible(false)
  }

  useEffect(() => {
    getData(current, pageSize)
  }, [current, pageSize, username, activeStatus, getData])

  useEffect(() => {
    setCurrent(1)
    setPageSize(10)
  }, [username, activeStatus])

  const columns: TableProps<UserData>['columns'] = [
    {
      title: 'User Name',
      dataIndex: 'userName',
      key: 'userName',
      width: 200,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
    },
    {
      title: '2FA Status',
      dataIndex: 'isEnable2Fa',
      key: 'isEnable2Fa',
      width: 120,
      render: (_, record) => {
        const handle2FaSwitchClick = () => {
          if (record.isEnable2Fa) {
            Modal.confirm({
              title: 'Are you sure you want to reset 2FA?',
              content: 'This action will reset the 2FA status for this account.',
              okText: 'Yes, Reset 2FA',
              cancelText: 'Cancel',
              centered: true,
              onOk: () => {
                handleUpdate({ code: record.code, isResetF2a: true })
              },
            })
          }
        }

        return (
          <Switch
            checked={record.isEnable2Fa}
            onClick={handle2FaSwitchClick}
            disabled={!record.isEnable2Fa}
          />
        )
      },
    },
    {
      title: 'Active Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      render: (_, record) => {
        const handleSwitchClick = () => {
          if (record.isActive) {
            Modal.confirm({
              title: 'Are you sure you want to deactivate this account?',
              content: 'This action will disable access for this account.',
              okText: 'Yes, Deactivate',
              cancelText: 'Cancel',
              centered: true,
              onOk: () => {
                handleUpdate({ code: record.code, isActive: false })
              },
            })
          } else {
            Modal.confirm({
              title: 'Are you sure you want to activate this account?',
              content: 'This action will grant access to this account.',
              okText: 'Yes, Grant Access',
              cancelText: 'Cancel',
              centered: true,
              onOk: () => {
                handleUpdate({ code: record.code, isActive: true })
              },
            })
          }
        }

        return <Switch checked={record.isActive} onClick={handleSwitchClick} />
      },
    },
    {
      title: 'Role',
      dataIndex: 'userRole',
      key: 'userRole',
      width: 240,
      render: (_, record) => {
        const handleRoleChange = (nextRole: USER_ROLE) => {
          if (nextRole === record.userRole) return

          Modal.confirm({
            title: 'Change user role?',
            content: `This will change the role to "${USER_ROLE_LABELS[nextRole]}". Permissions will be updated accordingly.`,
            okText: 'Yes, Change Role',
            cancelText: 'Cancel',
            centered: true,
            onOk: () => {
              handleUpdate({ code: record.code, roles: [USER_ROLE_MARKERS[nextRole]] })
            },
          })
        }

        const isRecordSuperAdmin = record.userRole === USER_ROLE.SUPER_ADMIN
        // Existing Super Admin rows are locked: managed only via BE/DB.
        // Keep full options here so the "Super Admin" label still renders on the disabled Select.
        const rowOptions = isRecordSuperAdmin ? USER_ROLE_OPTIONS : ASSIGNABLE_ROLE_OPTIONS

        return (
          <Select<USER_ROLE>
            style={{ width: '100%' }}
            placeholder="Select role"
            value={record.userRole ?? undefined}
            onChange={handleRoleChange}
            options={rowOptions}
            disabled={isRecordSuperAdmin}
          />
        )
      },
    },
    {
      title: 'Action',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Button onClick={() => showResetPasswordModal(record.code)}>Reset Password</Button>
      ),
    },
  ]

  const handleTableChange = (pagination: TablePaginationConfig) => {
    const { current: newPage, pageSize: newPageSize } = pagination
    setCurrent(newPage || 1)
    setPageSize(newPageSize || 10)
  }

  const [passwordVisible, setPasswordVisible] = useState(false)

  useEffect(() => {
    setPasswordVisible(false)
  }, [isModalVisible, isCreateUserModalVisible])

  const openCreateUserModal = () => {
    setNewUserName('')
    setNewUserRole(undefined)
    setNewUserPassword(generateRandomPassword())
    setIsCreateUserModalVisible(true)
  }

  return (
    <div className="main-section user">
      <Breadcrumb additionalItems={additionalItems} />
      <div className="main-title" style={{ marginBottom: 20 }}>
        User
      </div>
      <div className="user-search">
        <input
          type="text"
          placeholder="Username"
          value={username}
          onChange={(e) => setUserName(e.target.value)}
        />
        <Select
          placeholder="Active Status"
          allowClear
          options={[
            { value: -1, label: 'All' },
            { value: 1, label: 'Active' },
            { value: 0, label: 'Unactive' },
          ]}
          value={activeStatus}
          onChange={(value) => setActiveStatus(value)}
          style={{ width: 170, textAlign: 'start', height: 38 }}
        />
        <Button
          type="primary"
          onClick={() => {
            setCurrent(1)
            setPageSize(10)
            getData(1, 10)
          }}
        >
          Search
        </Button>
        <Button type="primary" onClick={openCreateUserModal}>
          Create New User
        </Button>
      </div>
      <Card style={{ margin: '0 auto' }}>
        <Table<UserData>
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={total < 11 ? false : { total, current, pageSize }}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
          bordered
        />
      </Card>
      <Modal
        title="Reset Password"
        visible={isModalVisible}
        onOk={() => {
          Modal.confirm({
            title: 'Are you sure you want to reset the password?',
            content:
              "This action will reset the user's password. Are you sure you want to proceed?",
            okText: 'Yes, Reset',
            cancelText: 'Cancel',
            centered: true,
            onOk: handleResetPassword,
          })
        }}
        onCancel={() => setIsModalVisible(false)}
        okText="Reset Password"
        cancelText="Cancel"
        centered
      >
        <Input.Password
          placeholder="Enter new password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          visibilityToggle={{ visible: passwordVisible, onVisibleChange: setPasswordVisible }}
        />
      </Modal>
      <Modal
        title="Create New User"
        visible={isCreateUserModalVisible}
        onOk={handleRegister}
        onCancel={() => setIsCreateUserModalVisible(false)}
        okText="Create User"
        cancelText="Cancel"
        centered
        destroyOnClose
      >
        <Form layout="vertical">
          <Form.Item label="Username" required>
            <Input
              placeholder="Enter username"
              value={newUserName}
              onChange={(e) => setNewUserName(e.target.value)}
            />
          </Form.Item>
          <Form.Item label="Password" required>
            <Input.Password
              placeholder="Enter password"
              value={newUserPassword}
              onChange={(e) => setNewUserPassword(e.target.value)}
              visibilityToggle={{ visible: passwordVisible, onVisibleChange: setPasswordVisible }}
            />
          </Form.Item>
          <Form.Item label="Role" required>
            <Select<USER_ROLE>
              placeholder="Select role"
              value={newUserRole}
              onChange={(value) => setNewUserRole(value)}
              options={ASSIGNABLE_ROLE_OPTIONS}
            />
            {newUserRole && (
              <Tag color={USER_ROLE_TAG_COLORS[newUserRole]} style={{ marginTop: 8 }}>
                {USER_ROLE_DESCRIPTIONS[newUserRole]}
              </Tag>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
